import {Component, Input, OnDestroy, OnInit} from "@angular/core";
import {FormControl, FormGroup} from "@angular/forms";
import {MatTabChangeEvent} from "@angular/material/tabs";
import {ICompanionForm} from "../../../../shared/interfaces/forms/exercises/ICompanionForm";
import {ValidationHelperService} from "../../../../core/helpers/validation-helper.service";
import {ECompanionMode} from "../../../../shared/enums/ECompanionMode";
import {EAction} from "../../../../shared/enums/EAction";
import {Subscription} from "rxjs";

@Component({
  selector: "app-companion-exercise-form",
  templateUrl: "./companion-exercise-form.component.html",
  styleUrls: ["./companion-exercise-form.component.scss"],
})
export class CompanionExerciseFormComponent implements OnInit, OnDestroy {
  @Input() _formGroup: FormGroup<ICompanionForm>;
  @Input() action: EAction = EAction.Add;
  private readonly subscriptions = new Subscription();

  ngOnInit(): void {
    this.subscribeToFormChanges();
    // Trigger initial validation
    this._formGroup.updateValueAndValidity();
  }

  private subscribeToFormChanges(): void {
    // Subscribe to changes in companion mode, number of companions, and number of sensors
    this.subscriptions.add(this.companionMode.valueChanges.subscribe(() => {
      this._formGroup.markAsTouched();
      this._formGroup.updateValueAndValidity();
    }));

    this.subscriptions.add(this.numberOfCompanions.valueChanges.subscribe(() => {
      this._formGroup.markAsTouched(); 
      this._formGroup.updateValueAndValidity();
    }));

    this.subscriptions.add(this.numberOfSensors.valueChanges.subscribe(() => {
      this._formGroup.markAsTouched();
      this._formGroup.updateValueAndValidity();
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get companionMode(): FormControl<ECompanionMode> { return this._formGroup.get('companionMode') as FormControl<ECompanionMode>; }
  get numberOfCompanions(): FormControl<number> { return this._formGroup.get('numberOfCompanions') as FormControl<number>; }
  get numberOfSensors(): FormControl<number> { return this._formGroup.get('numberOfSensors') as FormControl<number>; }

  onTabChange(event: MatTabChangeEvent): void {
    // Map tab index to ECompanionMode enum values
    const modeMap: ECompanionMode[] = [
      ECompanionMode.Simultaneous, // index 0
      ECompanionMode.React,        // index 1
      ECompanionMode.Agility       // index 2
    ];
    
    const selectedMode = modeMap[event.index];
    if (selectedMode !== undefined && this.action !== EAction.Edit) {
      this.companionMode.setValue(selectedMode);
      this._formGroup.updateValueAndValidity();
    }
  }

  hasCompanionConfigurationError(): boolean {
    return this._formGroup.errors?.invalidCompanionConfiguration === true && this._formGroup.touched;
  }

  getCompanionConfigurationErrorTitle(): string {
    if (this.companionMode.value === ECompanionMode.Agility) {
      return "Adjust companion setup for Agility";
    }
    if (this.companionMode.value === ECompanionMode.Simultaneous) {
      return "Adjust companion setup for Simultaneous";
    }
    return "Adjust companion setup";
  }

  getCompanionConfigurationErrorMessage(): string {
    const errors = this._formGroup.errors;
    if (!errors?.invalidCompanionConfiguration) {
      return '';
    }

    if (this.companionMode.value === ECompanionMode.Simultaneous) {
      return `Use 2 or 3 companions, or use 1 companion with 2–5 sensors. Current: ${this.numberOfCompanions.value} companion(s), ${this.numberOfSensors.value} sensor(s).`;
    } else if (this.companionMode.value === ECompanionMode.Agility) {
      return `Use 1 companion with 2–5 sensors, or 2 companions with 1–5 sensors. Current: ${this.numberOfCompanions.value} companion(s), ${this.numberOfSensors.value} sensor(s).`;
    }

    return 'Invalid companion configuration for this mode.';
  }

  protected readonly ValidationHelperService = ValidationHelperService;
  protected readonly ECompanionMode = ECompanionMode;
  protected readonly EAction = EAction;
}
